/* eslint-disable no-param-reassign */
/* eslint-disable react/require-default-props */
/* eslint-disable react/prop-types */
import React, {
  forwardRef, Ref, useContext, useRef, useCallback, useEffect,
} from 'react';
import { isFunction } from 'underscore';
// eslint-disable-next-line import/no-extraneous-dependencies
import { usePrevious } from '@bingads-webui-react/primitive-hooks';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuButton,
  Tooltip,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import {
  QuestionCircleRegular,
} from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { withHotKeys, ShortcutType } from '@bingads-webui-react/hot-keys';

import $ from 'jquery';

import './global';
import type { II18n } from './types';

const useStyles = makeStyles({
  button: {
    height: '48px',
    width: '48px',
    maxWidth: '48px',
  },
});

const getRegion = (i18n) => i18n.getCldr().attributes.region;
type HelpMenuProps = {
  as?: React.FC<{}>,
  itemKey: string,
  setIsFeedbackPanelOpen: React.Dispatch<React.SetStateAction<boolean>>,
  isFeedbackPanelOpen: boolean,
  hotKey?: string,
  count?: number,
  outSideRef?: React.Ref<HTMLElement>,
  isCopilotDisabled?: boolean,
};

const HelpMenu: React.FC<HelpMenuProps> = ({
  as,
  itemKey,
  setIsFeedbackPanelOpen,
  isFeedbackPanelOpen,
  hotKey,
  count,
  outSideRef: ref,
  isCopilotDisabled,
}) => {
  const instrumentation = useContext(InstrumentationContext) as any;
  const scenario = instrumentation.createScenario({ scenarioName: 'top-nav-dropdown-help-button' });
  const activity = scenario.createActivity('top-nav-dropdown-help-button');
  const initialRender = useRef(true);
  const previousCount = usePrevious(count);
  const classes = useStyles();
  const i18n: II18n = useContext(I18nContext);
  const globalHelpRef = useRef<HTMLAnchorElement>(null);
  const getStartedHelpRef = useRef<HTMLAnchorElement>(null);
  const prevIsFeedbackPanelOpen = usePrevious(isFeedbackPanelOpen);

  const returnFocus = useCallback(() => {
    const triggerButtonRef = ref as React.RefObject<HTMLButtonElement>;
    if (triggerButtonRef && triggerButtonRef.current && isFunction(triggerButtonRef.current.focus)) {
      triggerButtonRef.current.focus();
    }
  }, [ref]);
  const browserHelpTrigger = useCallback(() => {
    activity.signal(`${getRegion(i18n)} global help articles triggered`);
    if (globalHelpRef.current) {
      globalHelpRef.current.click();
    }
  }, [activity, i18n]);
  const getStartedTrigger = useCallback(() => {
    activity.signal(`${getRegion(i18n)} get started triggered`);
    if (getStartedHelpRef.current) {
      getStartedHelpRef.current.click();
    }
  }, [activity, i18n]);
  const showCommunityTrigger = useCallback(() => {
    activity.signal(`${getRegion(i18n)} show community triggered`);
    window.open('https://answers.microsoft.com/lang/msadvs/forum/', '_blank');
  }, [activity, i18n]);

  const chatTrigger = useCallback(() => {
    activity.signal(`${getRegion(i18n)} live chat without copilot triggered`);
    $('#msSupportChatTrigger').trigger('click', { onCancel: returnFocus });
  }, [activity, i18n, returnFocus]);

  const chatCopilotTrigger = useCallback(() => {
    activity.signal(`${getRegion(i18n)} copilot live agent support triggered from help menu`);

    const detail = {
      eventType: 'action',
      eventData: {
        ActionId: 'liveAgentSupport',
        FlowId: 'Copilot-Help-StartLiveChat',
      },
    };

    document.dispatchEvent(new CustomEvent('copilotToggleEvent', { detail }));
  }, [activity, i18n]);

  const callTrigger = useCallback(() => {
    activity.signal(`${getRegion(i18n)} request a call triggered`);
    $('#requestCallTrigger').trigger('click', { onCancel: returnFocus });
  }, [activity, i18n, returnFocus]);

  const openFeedbackPanel = useCallback(() => {
    activity.signal(`${getRegion(i18n)} open feedback panel triggered`);
    setIsFeedbackPanelOpen(true);
  }, [activity, i18n, setIsFeedbackPanelOpen]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    if (prevIsFeedbackPanelOpen && !isFeedbackPanelOpen) {
      activity.signal(`${getRegion(i18n)} close feedback panel triggered`);
      setIsFeedbackPanelOpen(false);
      returnFocus();
    }
  }, [activity, i18n, isFeedbackPanelOpen, prevIsFeedbackPanelOpen, returnFocus, setIsFeedbackPanelOpen]);

  useEffect(() => {
    if (hotKey === 'shift+h' && count !== previousCount) {
      activity.signal(`${getRegion(i18n)} global help shortcut triggered`);
      if ($('#apexHelpContainer').is(':visible')) {
        $('#apexHelpContainer .close_button').click();
      } else if (globalHelpRef.current) {
        globalHelpRef.current.click();
      }
    }
  }, [activity, count, hotKey, i18n, previousCount]);

  return (
    <>
      <Menu key={itemKey}>
        <MenuTrigger>
          {(as === MenuButton) ? (
            <Tooltip content="Help" relationship="label">
              <MenuButton
                className={mergeClasses(classes.button, 'nav-item-menu')}
                icon={<QuestionCircleRegular />}
                appearance="subtle"
                id="help-menu-button"
                ref={ref as Ref<HTMLButtonElement>}
              />
            </Tooltip>
          ) : (
            <MenuItem
              className="nav-item-menu"
              icon={<QuestionCircleRegular />}
              ref={ref as Ref<HTMLDivElement>}
              id="help-menu-item"
            >
              {i18n.getString(_TL_('Help'))}
            </MenuItem>
          )}
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              className="help-menu-item"
              id="help-article-link"
              onClick={browserHelpTrigger}
            >
              {i18n.getString(_TL_('Browse help articles'))}
            </MenuItem>
            <MenuItem
              className="help-menu-item"
              id="get-stared-link"
              onClick={getStartedTrigger}
            >
              {i18n.getString(_TL_('Get started'))}
            </MenuItem>
            <MenuItem
              className="help-menu-item"
              id="community-link"
              onClick={showCommunityTrigger}
            >
              {i18n.getString(_TL_('Ask the community'))}
            </MenuItem>
            {!isCopilotDisabled && (
              <MenuItem
                className="help-menu-item"
                id="help-chat"
                onClick={chatCopilotTrigger}
              >
                {i18n.getString(_TL_('Start a live chat'))}
              </MenuItem>
            )}
            {isCopilotDisabled && document.querySelector('#msSupportChatTrigger') && (
              <MenuItem
                className="help-menu-item"
                id="help-chat"
                onClick={chatTrigger}
              >
                {i18n.getString(_TL_('Start a live chat'))}
              </MenuItem>
            )}
            {document.querySelector('#requestCallTrigger')
              && (
                <MenuItem
                  className="help-menu-item"
                  id="help-request-call"
                  onClick={callTrigger}
                >
                  {i18n.getString(_TL_('Request a call from support'))}
                </MenuItem>
              )}
            <MenuItem
              className="help-menu-item"
              id="feedback-link"
              onClick={openFeedbackPanel}
            >
              {i18n.getString(_TL_('Send us feedback'))}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
      <div style={{ display: 'none' }}>
        <a ref={globalHelpRef} className={mergeClasses('HelpTopic', 'global-help')} href="/#"> </a>
        <a ref={getStartedHelpRef} className={mergeClasses('HelpTopic', 'get-started')} href="/#" query="ext56873"> </a>
      </div>
    </>
  );
};

const HelpMenuWithPermissions = withPermissions(HelpMenu);

const HelpMenuWithHotKeys = withHotKeys(HelpMenuWithPermissions, ShortcutType.ShowHideHelp);

const HelpMenuWithHotKeysRef = forwardRef<HTMLElement, HelpMenuProps>((props, ref) => (
  <HelpMenuWithHotKeys {...props} outSideRef={ref} />
));

export { HelpMenuWithHotKeysRef as HelpMenu };
